import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AGENT_NAV } from "@/lib/agent-nav";
import { Checkbox } from "@/components/ui/checkbox";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { TrainingProgress } from "@/components/agent/TrainingProgress";
import { ThisWeeksFocus } from "@/components/agent/ThisWeeksFocus";
import { AdvisorOnboardingJourney } from "@/components/agent/AdvisorOnboardingJourney";
import { YourTeamSection } from "@/components/agent/YourTeamSection";

export const Route = createFileRoute("/agent")({
  head: () => ({
    meta: [
      { title: "Strategist Portal — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AgentPortal,
});

function AgentPortal() {
  const location = useLocation();
  const isOverview = location.pathname === "/agent";

  return (
    <ProtectedRoute requiredRole="agent">
      {isOverview ? (
        <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour" headerVariant="navy">
          <AgentDashboard />
        </PortalShell>
      ) : (
        <Outlet />
      )}
    </ProtectedRoute>
  );
}

interface Stats {
  mtd: number;
  ytd: number;
  activeClients: number;
  openLeads: number;
  pendingCommissions: number;
  recruitsMtd: number;
  series: Array<{ month: string; value: number }>;
  conversionRate: number;
  avgPolicySize: number;
  avgPremium: number;
  topSource: { name: string; rate: number } | null;
}
interface Task {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
  payload: { milestone_id?: string; policy_id?: string } | null;
}
interface Activity {
  id: string;
  action: string;
  created_at: string;
  target_type: string | null;
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function StatSeal({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-navy-900 border border-bronze-600/40 rounded-[4px] p-5 shadow-soft flex flex-col items-center text-center">
      <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.22em] text-bronze-400">{label}</p>
      <p className="mt-2 font-display font-semibold text-bronze-400 text-[26px] leading-none">{value}</p>
    </div>
  );
}

function AgentDashboard() {
  const { user, profile } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);

  useEffect(() => {
    if (!user) return;
    void load();
  }, [user]);

  const load = async () => {
    if (!user) return;
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
    const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

    const [polRes, clientsRes, leadsRes, commRes, tasksRes, actRes, leadsAllRes] = await Promise.all([
      supabase
        .from("policies")
        .select("face_amount, effective_date, status")
        .eq("agent_id", user.id)
        .gte("effective_date", startOfYear.slice(0, 10)),
      supabase.from("clients").select("id", { count: "exact", head: true }).eq("assigned_agent_id", user.id),
      supabase.from("leads").select("id", { count: "exact", head: true })
        .eq("assigned_agent_id", user.id).not("stage", "in", "(converted,lost)"),
      supabase.from("commissions").select("amount, paid_at").eq("agent_id", user.id).is("paid_at", null),
      supabase.from("tasks").select("id, title, status, due_date, payload")
        .eq("assignee_id", user.id).gte("due_date", todayStart.toISOString().slice(0, 10))
        .lte("due_date", todayEnd.toISOString().slice(0, 10)).order("due_date"),
      supabase.from("activity_logs").select("id, action, created_at, target_type")
        .eq("user_id", user.id).order("created_at", { ascending: false }).limit(8),
      supabase.from("leads").select("stage, source, created_at")
        .eq("assigned_agent_id", user.id)
        .gte("created_at", ninetyDaysAgo),
    ]);

    const policies = (polRes.data ?? []) as Array<{ face_amount: number | null; effective_date: string | null; status: string }>;
    const ytd = policies.reduce((s, p) => s + (Number(p.face_amount) || 0), 0);
    const mtd = policies
      .filter((p) => p.effective_date && p.effective_date >= startOfMonth.slice(0, 10))
      .reduce((s, p) => s + (Number(p.face_amount) || 0), 0);

    // 12-month series
    const series: Array<{ month: string; value: number }> = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleDateString("en-US", { month: "short" });
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const total = policies
        .filter((p) => p.effective_date?.startsWith(monthKey))
        .reduce((s, p) => s + (Number(p.face_amount) || 0), 0);
      series.push({ month: label, value: total });
    }

    const pendingComm = (commRes.data ?? []).reduce((s, c) => s + (Number(c.amount) || 0), 0);

    // Expanded metrics
    const allLeads = (leadsAllRes.data ?? []) as Array<{ stage: string; source: string | null; created_at: string }>;
    const totalLeads = allLeads.length;
    const convertedLeads = allLeads.filter((l) => l.stage === "converted").length;
    const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;

    // Avg policy face & premium YTD
    const ytdPolicies = await supabase
      .from("policies")
      .select("face_amount, premium")
      .eq("agent_id", user.id)
      .gte("effective_date", startOfYear.slice(0, 10));
    const polRows = (ytdPolicies.data ?? []) as Array<{ face_amount: number | null; premium: number | null }>;
    const avgPolicySize = polRows.length > 0
      ? polRows.reduce((s, p) => s + (Number(p.face_amount) || 0), 0) / polRows.length
      : 0;
    const avgPremium = polRows.length > 0
      ? polRows.reduce((s, p) => s + (Number(p.premium) || 0), 0) / polRows.length
      : 0;

    // Top lead source
    const sourceMap = new Map<string, { total: number; converted: number }>();
    for (const l of allLeads) {
      const key = l.source || "unknown";
      const m = sourceMap.get(key) ?? { total: 0, converted: 0 };
      m.total += 1;
      if (l.stage === "converted") m.converted += 1;
      sourceMap.set(key, m);
    }
    let topSource: { name: string; rate: number } | null = null;
    let bestScore = -1;
    for (const [name, { total, converted }] of sourceMap.entries()) {
      const rate = total > 0 ? (converted / total) * 100 : 0;
      // prefer most leads; tie-break by rate
      const score = total * 100 + rate;
      if (score > bestScore) {
        bestScore = score;
        topSource = { name, rate };
      }
    }

    setStats({
      mtd, ytd,
      activeClients: clientsRes.count ?? 0,
      openLeads: leadsRes.count ?? 0,
      pendingCommissions: pendingComm,
      recruitsMtd: 0,
      series,
      conversionRate,
      avgPolicySize,
      avgPremium,
      topSource,
    });
    setTasks((tasksRes.data ?? []) as Task[]);
    setActivity((actRes.data ?? []) as Activity[]);
  };

  const markDone = async (id: string) => {
    await supabase.from("tasks").update({ status: "done" }).eq("id", id);
    setTasks((t) => t.filter((x) => x.id !== id));
    toast.success("Task completed");
  };

  const firstName = (profile?.full_name || "").split(" ")[0] || "Strategist";

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "36px", letterSpacing: "-0.01em" }}>
        Welcome, Strategist <em className="text-bronze-600 italic font-normal">{firstName}</em>
      </h2>

      <AdvisorOnboardingJourney />

      <div data-tour="agent-kpis" className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatSeal label="MTD Production" value={fmtCurrency(stats?.mtd ?? 0)} />
        <StatSeal label="YTD Production" value={fmtCurrency(stats?.ytd ?? 0)} />
        <StatSeal label="Active Clients" value={String(stats?.activeClients ?? 0)} />
        <StatSeal label="Open Leads" value={String(stats?.openLeads ?? 0)} />
        <StatSeal label="Pending Comm." value={fmtCurrency(stats?.pendingCommissions ?? 0)} />
        <StatSeal label="Recruits MTD" value={String(stats?.recruitsMtd ?? 0)} />
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatSeal label="Conversion 90d" value={`${(stats?.conversionRate ?? 0).toFixed(1)}%`} />
        <StatSeal label="Avg Policy Size" value={fmtCurrency(stats?.avgPolicySize ?? 0)} />
        <StatSeal label="Avg Premium" value={fmtCurrency(stats?.avgPremium ?? 0)} />
        <Link
          to="/agent/pipeline"
          search={stats?.topSource ? { source: stats.topSource.name } as any : undefined}
          className="bg-navy-900 border border-bronze-600/40 rounded-[4px] p-5 shadow-soft flex flex-col items-center text-center hover:border-bronze-400 transition-colors"
        >
          <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.22em] text-bronze-400">Top Source</p>
          <p className="mt-2 font-display font-semibold text-bronze-400 text-[18px] leading-tight">
            {stats?.topSource?.name ?? "—"}
          </p>
          <p className="mt-1 font-sans text-[11px] text-bronze-400/70">
            {stats?.topSource ? `${stats.topSource.rate.toFixed(0)}% conv.` : ""}
          </p>
        </Link>
      </div>

      <YourTeamSection />

      <div data-tour="agent-production" className="mt-8 bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          Production · Last 12 Months
        </p>
        <div className="mt-4" style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={stats?.series ?? []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="prodFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0A3369" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0A3369" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#A88B5C" strokeOpacity={0.2} strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12, fontFamily: "Inter" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 12, fontFamily: "Inter" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 4, fontFamily: "Inter", fontSize: 12 }}
                formatter={(v: number) => [fmtCurrency(v), "Production"]}
              />
              <Area type="monotone" dataKey="value" stroke="#A88B5C" strokeWidth={2} fill="url(#prodFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrainingProgress />
        <ThisWeeksFocus />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">Today's Tasks</p>
          <ul className="mt-4 divide-y divide-bronze-600/15">
            {tasks.length === 0 && (
              <li className="py-6 text-center font-sans text-[13px] text-charcoal-500">Nothing due today.</li>
            )}
            {tasks.map((t) => (
              <li key={t.id} className="py-3 flex items-center gap-3">
                <Checkbox checked={t.status === "done"} onCheckedChange={() => void markDone(t.id)} className="border-bronze-600" />
                <span className="flex-1 font-sans text-[13px] text-charcoal-900">{t.title}</span>
                {t.payload?.milestone_id ? (
                  <span className="px-2 py-0.5 bg-bronze-600 text-cream-50 font-sans text-[10px] uppercase tracking-[0.12em] rounded-full">
                    Review
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-bronze-600/15 text-bronze-600 font-sans text-[10px] uppercase tracking-[0.12em] rounded-full">
                    Today
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">Recent Activity</p>
          <ul className="mt-4 divide-y divide-bronze-600/15">
            {activity.length === 0 && (
              <li className="py-6 text-center font-sans text-[13px] text-charcoal-500">No recent activity.</li>
            )}
            {activity.map((a) => (
              <li key={a.id} className="py-2.5">
                <p className="font-sans text-[13px] text-charcoal-900">{a.action.replace(/[._]/g, " ")}</p>
                <p className="font-sans text-[11px] text-charcoal-500 mt-0.5">
                  {new Date(a.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Add Lead", href: "/agent/leads" as const },
          { label: "Add Client", href: "/agent/clients" as const },
          { label: "Upload Policy", href: "/agent" as const },
          { label: "Book Appointment", href: "/book" as const },
        ].map((a) => (
          <Link key={a.label} to={a.href}
            className="text-center py-3 border border-bronze-600 text-bronze-600 hover:bg-bronze-600 hover:text-navy-900 font-sans text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors rounded-[2px]">
            {a.label}
          </Link>
        ))}
      </div>
    </div>
  );
}